import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Upload, ArrowUp, ArrowDown, Trash2, Eye, EyeOff, Tablet, ExternalLink } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const inputCls = "w-full bg-black border border-white/15 px-3 py-2 text-sm text-[#F5F2EB] placeholder-[#52525B] focus:outline-none focus:border-[#D4AF37]";

/** Dispatcher: upload / order / toggle the idle-screen promo slides shown on /passenger. */
export default function KioskAdsManager() {
  const [data, setData] = useState(null);
  const fileRef = useRef(null);
  const [draft, setDraft] = useState({ title: "", subtitle: "", file: null });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/kiosk/slides").then(r => setData(r.data)).catch(() => setData({ slides: [], defaults: [], show_defaults: true, interval_seconds: 8 }));
  useEffect(() => { load(); }, []);

  const upload = async () => {
    if (!draft.file) { toast.error("Choose an image first"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", draft.file); fd.append("title", draft.title); fd.append("subtitle", draft.subtitle);
      await api.post("/kiosk/slides", fd);
      toast.success("Slide added to the kiosk");
      setDraft({ title: "", subtitle: "", file: null }); if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  const move = async (idx, dir) => {
    const ids = data.slides.map(s => s.slide_id);
    const j = idx + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    const r = await api.put("/kiosk/slides/reorder", { slide_ids: ids });
    setData(d => ({ ...d, slides: r.data.slides }));
  };

  const patch = async (slide_id, body) => {
    const r = await api.patch(`/kiosk/slides/${slide_id}`, body);
    setData(d => ({ ...d, slides: d.slides.map(s => s.slide_id === slide_id ? r.data : s) }));
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete slide "${s.title || "Untitled"}"?`)) return;
    await api.delete(`/kiosk/slides/${s.slide_id}`); toast.success("Slide deleted"); load();
  };

  const saveSettings = async (body) => {
    const r = await api.put("/kiosk/settings", body);
    setData(d => ({ ...d, ...r.data }));
  };

  if (!data) return null;

  return (
    <div className="mt-12 border border-white/10 bg-[#0A0A0A] p-6" data-testid="kiosk-ads-manager">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <div className="overline mb-1 flex items-center gap-2"><Tablet size={12}/> Passenger screen</div>
          <h3 className="font-serif text-2xl">Manage Kiosk Ads</h3>
          <p className="text-xs text-[#A1A1AA] mt-1">Promo slides shown on the rear-seat tablet when no trip is active. Your uploads play first, then the default brand slides.</p>
        </div>
        <div className="flex flex-wrap items-center gap-4 text-xs">
          <label className="flex items-center gap-2 text-[#A1A1AA]">
            <input data-testid="kiosk-show-defaults" type="checkbox" checked={data.show_defaults !== false} onChange={e => saveSettings({ show_defaults: e.target.checked })} className="accent-[#D4AF37]"/>
            Include default brand slides
          </label>
          <label className="flex items-center gap-2 text-[#A1A1AA]">
            Every
            <select data-testid="kiosk-interval" value={data.interval_seconds || 8} onChange={e => saveSettings({ interval_seconds: parseInt(e.target.value, 10) })} className="bg-black border border-white/15 px-2 py-1 text-[#F5F2EB]">
              {[5, 8, 10, 15, 20, 30].map(n => <option key={n} value={n}>{n}s</option>)}
            </select>
          </label>
          <a data-testid="kiosk-preview-link" href="/passenger" target="_blank" rel="noreferrer" className="btn-ghost !py-2 !px-3 text-[10px] inline-flex items-center gap-1"><ExternalLink size={12}/> Preview kiosk</a>
        </div>
      </div>

      <div className="grid lg:grid-cols-[320px_1fr] gap-8">
        <div className="border border-dashed border-white/15 p-4 space-y-3" data-testid="kiosk-upload-form">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#A1A1AA]">Add a slide</div>
          <input ref={fileRef} data-testid="kiosk-file-input" type="file" accept="image/jpeg,image/png,image/webp"
            onChange={e => setDraft(d => ({ ...d, file: e.target.files?.[0] || null }))}
            className="block w-full text-xs text-[#A1A1AA] file:mr-3 file:border file:border-white/20 file:bg-transparent file:text-[#F5F2EB] file:px-3 file:py-1.5 file:text-[10px] file:uppercase file:tracking-[0.16em]"/>
          <input data-testid="kiosk-title-input" className={inputCls} placeholder="Headline (optional)" value={draft.title} onChange={e => setDraft(d => ({ ...d, title: e.target.value }))} maxLength={80}/>
          <input data-testid="kiosk-subtitle-input" className={inputCls} placeholder="Supporting line (optional)" value={draft.subtitle} onChange={e => setDraft(d => ({ ...d, subtitle: e.target.value }))} maxLength={200}/>
          <button data-testid="kiosk-upload-btn" disabled={busy || !draft.file} onClick={upload} className="btn-gold !py-2.5 !px-4 text-[10px] w-full justify-center disabled:opacity-40">
            <Upload size={12}/> {busy ? "Uploading…" : "Upload slide"}
          </button>
          <p className="text-[10px] text-[#71717A]">JPG / PNG / WEBP up to 8 MB. Landscape 16:9 looks best on the tablet.</p>
        </div>

        <div className="space-y-2" data-testid="kiosk-slide-list">
          {data.slides.length === 0 && <div className="text-sm text-[#A1A1AA] border border-white/10 p-6 text-center" data-testid="kiosk-slides-empty">No custom slides yet — the kiosk is showing the default brand slides.</div>}
          {data.slides.map((s, i) => (
            <div key={s.slide_id} data-testid={`kiosk-slide-${s.slide_id}`} className={`flex items-center gap-4 border border-white/10 p-3 ${s.active ? "" : "opacity-50"}`}>
              <div className="text-[10px] text-[#71717A] w-5 text-center">{i + 1}</div>
              <img src={`${BACKEND}${s.image_url}`} alt="" className="w-28 h-16 object-cover bg-black shrink-0"/>
              <div className="flex-1 min-w-0 grid gap-1.5">
                <input data-testid={`kiosk-slide-title-${s.slide_id}`} className={inputCls} defaultValue={s.title} placeholder="Headline" maxLength={80}
                  onBlur={e => e.target.value !== s.title && patch(s.slide_id, { title: e.target.value })}/>
                <input data-testid={`kiosk-slide-subtitle-${s.slide_id}`} className={inputCls} defaultValue={s.subtitle} placeholder="Supporting line" maxLength={200}
                  onBlur={e => e.target.value !== s.subtitle && patch(s.slide_id, { subtitle: e.target.value })}/>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <IconBtn testId={`kiosk-slide-up-${s.slide_id}`} onClick={() => move(i, -1)} disabled={i === 0} label="Move up"><ArrowUp size={14}/></IconBtn>
                <IconBtn testId={`kiosk-slide-down-${s.slide_id}`} onClick={() => move(i, 1)} disabled={i === data.slides.length - 1} label="Move down"><ArrowDown size={14}/></IconBtn>
                <IconBtn testId={`kiosk-slide-toggle-${s.slide_id}`} onClick={() => patch(s.slide_id, { active: !s.active })} label={s.active ? "Hide" : "Show"}>{s.active ? <Eye size={14}/> : <EyeOff size={14}/>}</IconBtn>
                <IconBtn testId={`kiosk-slide-delete-${s.slide_id}`} onClick={() => remove(s)} label="Delete" danger><Trash2 size={14}/></IconBtn>
              </div>
            </div>
          ))}
          {data.show_defaults !== false && (
            <div className="pt-3 text-[10px] uppercase tracking-[0.2em] text-[#71717A]" data-testid="kiosk-defaults-note">
              + {data.defaults.length} default brand slides ({data.defaults.map(d => d.title).join(" · ")})
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function IconBtn({ children, onClick, disabled, label, danger, testId }) {
  return (
    <button data-testid={testId} onClick={onClick} disabled={disabled} aria-label={label} title={label}
      className={`w-8 h-8 border flex items-center justify-center transition-colors disabled:opacity-25 ${danger ? "border-red-500/30 text-red-400 hover:bg-red-500/10" : "border-white/15 text-[#A1A1AA] hover:text-[#D4AF37] hover:border-[#D4AF37]"}`}>
      {children}
    </button>
  );
}
